import React, { useMemo, useEffect } from 'react';
import {
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import { withTranslation } from 'react-i18next';
import {
    Box,
    DaTable,
    DaTableBody,
    DaTableCell,
    DaTableHeadCell,
    DaTableHead,
    DaTableRow,
    Text,
    ThemeDefault,
} from '@paygreen/paygreen-ui';
import {
    timestampToDateWithHour,
    IsBigDesktop,
    IsMobile,
} from '../../../utils/tools';
import { statusOffsets } from '../../../utils/constants';
import {
    CarbonDataDisplay,
    HideAndCopy,
    PaginationTable,
    StatusDisplayTable,
    StatusFilterTable,
} from '../../../components';
import ActionsCell from './ActionsCell';
import { statusOptions } from './statusOptions';

const OffsetsTable = ({
    t,
    data,
    fetchData,
    loading,
    startDate,
    endDate,
    pageCount: controlledPageCount,
    totalItems,
    activePageReset,
    setActivePageReset,
    statusFilter,
    setStatusFilter,
}) => {
    // needed to guarantee vertical alignment on tablet for last cell in table
    const actionCellboxProps = {
        isMain: !IsMobile(),
        style: {
            display: !IsMobile() ? 'flex' : null,
            justifyContent: IsBigDesktop() ? 'center' : 'flex-end',
        },
    };

    // needed to guarantee vertical alignment and layout on mobile for status cell in table
    const statusCellboxStyle = {
        justifyContent: IsBigDesktop() ? null : 'flex-end',
        minWidth: IsMobile() ? `${ThemeDefault.blockHeader.xs}` : null,
        display: !IsBigDesktop() ? 'flex' : null,
    };

    const columns = useMemo(
        () => [
            {
                Header: t('offsets.table.fingerprint'),
                accessor: 'fingerprint',
            },
            {
                Header: t('offsets.table.date'),
                id: 'createdAt',
                accessor: (d) => timestampToDateWithHour(d.createdAt),
            },
            {
                Header: '',
                accessor: 'status',
                Filter: ({ filter, ...rest }) => (
                    <StatusFilterTable
                        defaultValue={statusFilter}
                        onChange={setStatusFilter}
                        setActivePageReset={setActivePageReset}
                        statusConstants={statusOffsets}
                        i18TextBase="offsets.status"
                        {...rest}
                    />
                ),
                filter: 'includes',
            },
            {
                Header: t('offsets.table.CO2'),
                id: 'estimatedCarbon',
                accessor: (d) => d.estimatedCarbon,
            },
            {
                Header: t('offsets.table.CO2ByThirdParty'),
                id: 'carbonOffsetByThirdParty',
                accessor: (d) => d.carbonOffsetByThirdParty,
            },
            {
                Header: t('offsets.table.money'),
                id: 'estimatedPrice',
                accessor: (d) => d.estimatedPrice,
            },
            {
                Header: 'Actions',
                accessor: 'idFootprint',
            },
        ],
        [t, setActivePageReset, statusFilter, setStatusFilter],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        pageCount,
        setPageSize,
        gotoPage,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 25 },
            manualPagination: true,
            pageCount: controlledPageCount,
            manualSortBy: true,
            autoResetPage: false,
            // we ponctually override table internal state to resetPage index with component SelectColumnFilter when needed
            useControlledState: (state) => {
                if (activePageReset) {
                    return { ...state, pageIndex: 0 };
                } else {
                    return state;
                }
            },
        },
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
    );

    useEffect(() => {
        fetchData({
            pageIndex,
            pageSize,
            startDate,
            endDate,
            statusFilter,
        });
    }, [
        fetchData,
        pageIndex,
        pageSize,
        startDate,
        endDate,
        statusFilter,
    ]);

    return (
        <Box>
            <DaTable
                {...getTableProps()}
                blockWidth="none"
                isLoading={loading}
                loadingRowNumber={15}
                loadingMainCellNumber={3}
            >
                {headerGroups.map((headerGroup) => (
                    <DaTableHead
                        blockTitle={t('user.list.table.title')}
                        {...headerGroup.getHeaderGroupProps()}
                    >
                        {headerGroup.headers.map((column, index) => (
                            <DaTableHeadCell
                                key={index}
                                label={column.render('Header')}
                                style={{
                                    maxWidth: `${ThemeDefault.blockHeader.md}`,
                                }}
                            >
                                {index === 2 ? column.render('Filter') : null}
                            </DaTableHeadCell>
                        ))}
                    </DaTableHead>
                ))}

                <DaTableBody {...getTableBodyProps()}>
                    {!loading && data.length ? (
                        page.map((row) => {
                            prepareRow(row);

                            return (
                                <DaTableRow {...row.getRowProps()} key={row.id}>
                                    <DaTableCell
                                        isMain={false}
                                        label={t('offsets.table.fingerprint')}
                                        title={row.values.fingerprint}
                                    >
                                        <HideAndCopy
                                            text={row.values.fingerprint}
                                            startLength={5}
                                            endLength={5}
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t('offsets.table.date')}
                                    >
                                        {row.values.createdAt}
                                    </DaTableCell>

                                    <DaTableCell
                                        style={{ ...statusCellboxStyle }}
                                    >
                                        <StatusDisplayTable
                                            status={row.values.status}
                                            statusOptions={statusOptions}
                                            i18TextBase="offsets.status"
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        isMain={false}
                                        label={t('offsets.table.CO2')}
                                    >
                                        <CarbonDataDisplay
                                            value={row.values.estimatedCarbon}
                                            mode="carbon"
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        isMain={false}
                                        label={t(
                                            'offsets.table.CO2ByThirdParty',
                                        )}
                                    >
                                        <CarbonDataDisplay
                                            value={
                                                row.values
                                                    .carbonOffsetByThirdParty
                                            }
                                            mode="carbon"
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t('offsets.table.money')}
                                    >
                                        <CarbonDataDisplay
                                            value={row.values.estimatedPrice}
                                            mode="price"
                                            isApproximated={
                                                row.values.estimatedPrice > 0
                                            }
                                            title={
                                                row.values.estimatedPrice > 0
                                                    ? t(
                                                          'offsets.carbonPriceTitle',
                                                      )
                                                    : ''
                                            }
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        label="Actions"
                                        {...actionCellboxProps}
                                    >
                                        <ActionsCell
                                            idFootprint={row.values.idFootprint}
                                            value={row.values.estimatedCarbon}
                                        />
                                    </DaTableCell>
                                </DaTableRow>
                            );
                        })
                    ) : (
                        <Text
                            align="center"
                            marginTop="xl"
                            marginBottom="xl"
                            textSize="sm"
                        >
                            {t('noDataFound')}
                        </Text>
                    )}
                </DaTableBody>
            </DaTable>

            {totalItems > 25 ? (
                <PaginationTable
                    setPageSize={setPageSize}
                    pageCount={pageCount || 1}
                    pageIndex={pageIndex}
                    gotoPage={gotoPage}
                    setActivePageReset={setActivePageReset}
                />
            ) : null}
        </Box>
    );
};

export default withTranslation('common')(OffsetsTable);
